import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-sans font-semibold text-xl text-foreground mb-4 tracking-[-0.01em]">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-foreground/75">{children}</div>
    </section>
  );
}
function P({ children }) { return <p>{children}</p>; }
function Ul({ items }) {
  return (
    <ul className="list-none space-y-2 pl-0">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span style={{ color: "#8A8A8A" }} className="mt-0.5 shrink-0">—</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function TermsAndConditions() {
  const updated = "June 2026";
  return (
    <div className="min-h-screen bg-ink text-white">
      <Navigation />
      <div className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at top, rgba(138,138,138,0.06) 0%, transparent 50%)" }} />
      <div className="relative z-10 pt-32 pb-24 px-6 max-w-3xl mx-auto">
        <p className="overline mb-4">Legal</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-tight mb-3">
          Terms of <span className="gold-gradient-text italic">Service</span>
        </h1>
        <p className="text-foreground/50 text-sm mb-12">Last updated: {updated}</p>

        <div className="card-luxe p-8 sm:p-10">

          <Section title="1. Acceptance of Terms">
            <P>By booking a service, visiting this website, or using the Nisky Auto booking platform, you agree to be bound by these Terms of Service. If you do not agree, do not book or use our services. These terms constitute a legally binding agreement between you and Nisky Auto, operating in Niskayuna, New York.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="2. Services">
            <P>Nisky Auto provides mobile exterior and interior car detailing services by appointment only. All services are performed at the address specified during booking, within our designated service area.</P>
            <P>We reserve the right to refuse service to any vehicle or at any location we deem unsafe, inaccessible, or outside our service area at our sole discretion, with no obligation to provide a reason.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="3. Pricing and Payment">
            <P>All prices quoted through our booking platform are estimates based on vehicle type, size, and condition as reported by the customer. Final pricing may be adjusted at the time of service if the vehicle's actual condition, size, or accessibility differs materially from what was stated during booking.</P>
            <Ul items={[
              "All payments are due in full at the time of service, in cash or another accepted method agreed upon in advance.",
              "We do not process or store any credit card or payment information on our platform.",
              "Quoted prices include the standard detail service only. Additional services, products, or time required due to undisclosed vehicle condition may incur additional charges.",
              "Prices are subject to change between seasons without notice.",
            ]} />
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="4. Customer Responsibilities">
            <P>By booking a service, you agree to the following responsibilities:</P>
            <Ul items={[
              "Remove all personal belongings, valuables, cash, documents, and irreplaceable items from the vehicle before the appointment. Nisky Auto is not responsible for any items left in the vehicle.",
              "Ensure the vehicle is accessible at the scheduled time and location. If we cannot access the vehicle within a reasonable window, the appointment may be forfeited.",
              "Provide a safe, flat, outdoor area with sufficient space for our team to work around the vehicle.",
              "Accurately describe the vehicle's condition, including any pre-existing damage, during booking.",
              "Ensure access to a water source if required for your service type, unless otherwise arranged in advance.",
            ]} />
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="5. Pre-Existing Damage and Vehicle Condition">
            <P>We inspect vehicles before beginning work. By proceeding with a booking, you acknowledge and accept that:</P>
            <Ul items={[
              "Nisky Auto is not liable for any pre-existing scratches, swirl marks, paint chips, oxidation, dents, rust, trim damage, or any other condition present on the vehicle prior to our arrival.",
              "Certain materials, finishes, and conditions (including but not limited to matte paint, ceramic coatings not applied by us, peeling clear coat, heavily oxidized paint, and aftermarket modifications) may respond unpredictably to detailing products and processes. We will take reasonable precautions, but we accept no liability for adverse outcomes on these surfaces.",
              "We will notify you of any concerns we observe before beginning; however, proceeding with service constitutes your acceptance of the associated risks.",
            ]} />
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="6. Limitation of Liability">
            <P style={{ fontWeight: 600 }}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NISKY AUTO, ITS OWNERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF USE, LOSS OF PROFITS, OR PROPERTY DAMAGE, ARISING OUT OF OR IN CONNECTION WITH OUR SERVICES OR THESE TERMS.</P>
            <P>In all cases, our total aggregate liability to you for any claim arising out of or relating to a specific appointment shall not exceed the amount you paid for that appointment.</P>
            <P>Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages, so some of the above limitations may not apply to you.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="7. No Warranties">
            <P>OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT OUR SERVICES WILL MEET YOUR SPECIFIC EXPECTATIONS OR THAT ANY PARTICULAR RESULT WILL BE ACHIEVED.</P>
            <P>Detailing results vary depending on vehicle age, paint condition, prior maintenance history, and environmental factors. We do not guarantee a specific level of shine, cleanliness, or restoration beyond our best reasonable effort.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="8. Cancellations and No-Shows">
            <Ul items={[
              "You may cancel or request to reschedule your appointment through the booking status page at any time prior to the appointment.",
              "We reserve the right to cancel or reschedule appointments due to weather, equipment issues, staff unavailability, or other circumstances outside our control. We will notify you as soon as possible if this occurs.",
              "Repeated cancellations or no-shows may result in refusal of future bookings at our discretion.",
              "No cancellation fee is charged. However, if a cancellation occurs after our team has already arrived at your location, a travel fee may apply.",
            ]} />
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="9. Weather and Delays">
            <P>Our services are performed outdoors. We may delay, reschedule, or modify services due to rain, extreme heat, wind, or other weather conditions that would compromise service quality or the safety of our team. We will contact you to reschedule in the event of a weather cancellation. No penalty applies to weather-related reschedules on either side.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="10. Photography">
            <P>We may photograph vehicles before, during, and after service for quality assurance and portfolio purposes. By booking our services, you grant Nisky Auto a non-exclusive, royalty-free license to use photographs of your vehicle (excluding any identifying information such as license plates) on our website and social media channels. If you do not wish your vehicle to be photographed, please inform us at the time of your appointment.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="11. Indemnification">
            <P>You agree to indemnify, defend, and hold harmless Nisky Auto, its owners, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in any way connected with your breach of these Terms, your misrepresentation of vehicle condition, or your violation of any applicable law.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="12. Intellectual Property">
            <P>All content on this website, including text, graphics, logos, and design elements, is the property of Nisky Auto and may not be reproduced, distributed, or used without our express written permission.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="13. Governing Law and Disputes">
            <P>These Terms are governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions. Any dispute arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Schenectady County, New York.</P>
            <P>You agree to first attempt to resolve any dispute informally by contacting us directly. If a dispute cannot be resolved informally, it shall be settled by binding arbitration in accordance with the rules of the American Arbitration Association, unless both parties agree otherwise in writing.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="14. Changes to These Terms">
            <P>We may update these Terms at any time. Changes are effective immediately upon posting to this page, with the "last updated" date revised accordingly. Continued use of our booking platform or services after changes are posted constitutes your acceptance of the updated Terms.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="15. Severability">
            <P>If any provision of these Terms is found to be unenforceable or invalid under applicable law, that provision will be modified to the minimum extent necessary to make it enforceable, and the remaining provisions will continue in full force and effect.</P>
          </Section>

          <div className="subtle-divider my-8" />

          <Section title="16. Automated Data Collection and Security Logging">
            <P>By using this platform, you acknowledge and agree that our systems automatically collect your IP address and certain request metadata in connection with every booking action. This data is used exclusively for security purposes, including rate limiting, abuse detection, and investigation of fraudulent activity.</P>
            <P>We do not use automatically collected technical data for advertising or behavioral profiling. For more information on how this data is collected, stored, and retained, see our Privacy Policy.</P>
          </Section>

        </div>

        <p className="text-center text-xs text-foreground/30 mt-8">
          © {new Date().getFullYear()} Nisky Auto · Niskayuna, NY ·{" "}
          <Link to="/privacy" className="hover:text-foreground/60 underline">Privacy Policy</Link>
          {" · "}
          <Link to="/" className="hover:text-foreground/60 underline">Home</Link>
        </p>
      </div>
      <Footer />
    </div>
  );
}
